/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/calendar-range.config.ts
 *
 * Centralized calendar date range configuration to ensure all calendar stores
 * fetch and process data for consistent ranges.
 */

export interface CalendarRangeConfig {
  /** Days to look back from today */
  daysBack: number;
  /** Days to look forward from today */
  daysForward: number;
  /** Human-readable description */
  description: string;
}

/**
 * Calendar range requirements for different use cases.
 * These define the data needs, not just UI filtering.
 */
export const CALENDAR_RANGE_REQUIREMENTS = {
  /** Main calendar views (day/week/month) - minimal range for performance */
  MAIN_VIEW: {
    daysBack: 7, // 1 week back
    daysForward: 21, // 3 weeks forward
    description: 'Main calendar view range'
  } as CalendarRangeConfig,

  /** CalendarNav - needs broader context for navigation */
  NAV_VIEW: {
    daysBack: 7, // 1 week back
    daysForward: 14, // 2 weeks forward
    description: 'Calendar navigation range'
  } as CalendarRangeConfig,

  /** Overview - needs recent past events */
  OVERVIEW: {
    daysBack: 30, // 30 days back for "recent" events
    daysForward: 30, // 30 days forward for "upcoming" events
    description: 'Overview recent and upcoming events'
  } as CalendarRangeConfig,

  /** Agenda - needs extended future visibility */
  AGENDA: {
    daysBack: 7, // 1 week back for context
    daysForward: 70, // 10 weeks forward for planning
    description: 'Agenda extended future events (10 weeks)'
  } as CalendarRangeConfig
} as const;

/**
 * Calculate the combined range needed to satisfy all use cases.
 * This is what calendar.store.ts should fetch.
 */
export function getUnifiedCalendarRange(): CalendarRangeConfig {
  const allRequirements = Object.values(CALENDAR_RANGE_REQUIREMENTS);

  const maxDaysBack = Math.max(...allRequirements.map((r) => r.daysBack));
  const maxDaysForward = Math.max(...allRequirements.map((r) => r.daysForward));

  return {
    daysBack: maxDaysBack,
    daysForward: maxDaysForward,
    description: `Unified range (${maxDaysBack} back, ${maxDaysForward} forward)`
  };
}

/**
 * Calculate date range from today for a given configuration.
 */
export function calculateDateRange(
  config: CalendarRangeConfig,
  referenceDate = new Date()
): {
  start: Date;
  end: Date;
  startISO: string;
  endISO: string;
} {
  const start = new Date(referenceDate);
  start.setDate(referenceDate.getDate() - config.daysBack);

  const end = new Date(referenceDate);
  end.setDate(referenceDate.getDate() + config.daysForward);

  return {
    start,
    end,
    startISO: start.toISOString(),
    endISO: end.toISOString()
  };
}

/**
 * Get range for a specific use case.
 */
export function getRangeFor(
  useCase: keyof typeof CALENDAR_RANGE_REQUIREMENTS,
  referenceDate = new Date()
) {
  const config = CALENDAR_RANGE_REQUIREMENTS[useCase];
  return calculateDateRange(config, referenceDate);
}

/**
 * Get the unified range that satisfies all use cases.
 */
export function getUnifiedRange(referenceDate = new Date()) {
  const config = getUnifiedCalendarRange();
  return calculateDateRange(config, referenceDate);
}

/**
 * Debug helper to show all range requirements.
 */
export function debugRangeRequirements() {
  const unified = getUnifiedCalendarRange();

  console.log('📅 Calendar Range Requirements:', {
    requirements: CALENDAR_RANGE_REQUIREMENTS,
    unified,
    unifiedRange: calculateDateRange(unified)
  });

  return {
    requirements: CALENDAR_RANGE_REQUIREMENTS,
    unified,
    unifiedRange: calculateDateRange(unified)
  };
}
